import { useDispatch, useSelector } from 'react-redux';
import { setAuthorisedModule, setLoadCount } from '@stores/Modules/slice';
import { LinkedSubmodule, useLazyGetModulesQuery } from '../../api/userModules';
import { Module as ApiModule, ProjectedSubmodule } from '../../api/userModules';
import { Module, Submodule } from '@stores/Modules/types/modules';
import { isNullOrUndefined, updateSubmoduleOrdering } from '@utils/common';
import { RootState } from '@stores/store';
import {
  DEMO_USER_EMAILS,
  EXTERNAL_SUBMODULE_TYPES,
  GETTING_STARTED_DEMO_MODULES,
  GETTING_STARTED_MODULES,
  SubmoduleCategory,
  SUBMODULE_TYPE,
} from '@constants/constants';
import UserAuth from '@services/auth';

const EXCLUDED_SUBMODULE_TYPES = [
  'FUNDING_REQUEST',
  'FINANCIAL_REPORTING',
  'DEAL_SETUP_DASHBOARD',
  'SELF_SERVE',
  'CLIENT_USER_MANAGEMENT',
];

/** A pending-access (signed up, no data connected) user only has these submodules. */
const PENDING_ACCESS_SUBMODULE_TYPES: string[] = [
  SUBMODULE_TYPE.GETTING_STARTED,
  SUBMODULE_TYPE.CLIENT_USER_MANAGEMENT,
  SUBMODULE_TYPE.MARKET_SCAN,
];

const DISALLOWED_SUBMODULE_TYPES_FOR_CLIENT = [
  'FUTURE_COHORTS',
  'MODEL_SETTINGS',
  'MODEL_REQUESTS',
  'EXPOSURE_FORECASTING',
];

const filterExternalSubmodules = (modules: Module[]): Module[] => {
  modules.forEach((module) => {
    module.submodules =
      module.submodules?.filter(
        (submodule) => !EXTERNAL_SUBMODULE_TYPES.includes(submodule.submoduleType || ''),
      ) ?? [];
  });

  // Remove modules that have no submodules
  return modules.filter((module) => module.submodules && module.submodules.length > 0);
};

/** Checks whether a module contains the V2 Investor Report submodule. */
const isInvestorReportV2Preset = (module: Module): boolean => {
  for (const submodule of module.submodules ?? []) {
    if (submodule.submoduleType === SUBMODULE_TYPE.INVESTOR_REPORT_V2) {
      return true;
    }
  }
  return false;
};

/**
 * Ensures only one PvX Report submodule is active per module.
 * If a module has the V2 Investor Report, the legacy V1 submodule is removed
 * to prevent duplicate report entries in the sidebar. Modules left with no
 * submodules after filtering are excluded entirely.
 */
const filterForOnePvXReportModule = (modules: Module[]): Module[] => {
  modules.forEach((module) => {
    if (isInvestorReportV2Preset(module)) {
      module.submodules =
        module.submodules?.filter(
          (submodule) => submodule.submoduleType !== SUBMODULE_TYPE.INVESTOR_REPORT,
        ) ?? [];
    }
  });

  // Remove modules that have no submodules
  return modules.filter((module) => module.submodules && module.submodules.length > 0);
};

const getMetadata = (module: Module) => {
  let metadata: {
    projectedSubmodules?: ProjectedSubmodule[];
    linkedSubmodules?: LinkedSubmodule[];
  } = {};

  if (!isNullOrUndefined(module.metadata)) {
    try {
      metadata = JSON.parse(module.metadata);
    } catch (error) {
      metadata = {};
    }
  }

  return metadata;
};

const getRelatedSubmodule = (module: Module, linkedSubmodule: LinkedSubmodule) => {
  return module.submodules?.find(
    (submodule) => submodule.submoduleType === linkedSubmodule.linkedSubmoduleType,
  );
};

const constructLinkedSubmodule = (
  linkedSubmodule: LinkedSubmodule,
  relatedSubmodule: Submodule,
): Submodule => {
  const { id, defaultFilter, hasFullAccess } = relatedSubmodule;
  return {
    id,
    name: linkedSubmodule.name,
    url: linkedSubmodule.url,
    embedId: '',
    submoduleType: linkedSubmodule.submoduleType,
    defaultFilter,
    nativeFilterId: '',
    submoduleCategory: SubmoduleCategory.NATIVE,
    hasFullAccess,
  };
};

const processProjectedSubmodules = (
  module: Module,
  projectedSubmodules: ProjectedSubmodule[],
): void => {
  if (isNullOrUndefined(projectedSubmodules) || projectedSubmodules.length === 0) return;
  if (isNullOrUndefined(module.submodules) || module.submodules.length === 0) return;

  // Assuming that first submodule will be coming and projected submodules will be assigned with this id.
  const { id, defaultFilter, hasFullAccess } = module.submodules?.[0] ?? {};

  module.submodules = projectedSubmodules.map(
    (projectedSubmodule: ProjectedSubmodule): Submodule => ({
      id,
      name: projectedSubmodule.name,
      url: projectedSubmodule.url,
      embedId: '',
      submoduleType: projectedSubmodule.submoduleType,
      defaultFilter,
      nativeFilterId: '',
      submoduleCategory: SubmoduleCategory.NATIVE,
      hasFullAccess,
    }),
  );
};

const SubmoduleDisplayNameMap: Partial<Record<SUBMODULE_TYPE, string>> = {
  [SUBMODULE_TYPE.FINANCIAL_PROJECTIONS_V2]: 'Financial Projections',
};

// To override the submodule display name for the submodule types
const overrideSubmoduleDisplayName = (module: Module): void => {
  if (isNullOrUndefined(module.submodules) || module.submodules.length === 0) return;

  module.submodules = module.submodules.map((submodule) => {
    const resolvedSubmoduleDisplayName =
      SubmoduleDisplayNameMap[submodule.submoduleType as SUBMODULE_TYPE] ?? submodule.name;
    return { ...submodule, name: resolvedSubmoduleDisplayName };
  });
};

const processLinkedSubmodules = (module: Module, linkedSubmodules: LinkedSubmodule[]): void => {
  if (isNullOrUndefined(linkedSubmodules) || linkedSubmodules.length === 0) return;

  let linkedSubmodulesArray: Submodule[] = [];

  linkedSubmodules.forEach((linkedSubmodule) => {
    const relatedSubmodule = getRelatedSubmodule(module, linkedSubmodule);
    if (relatedSubmodule) {
      linkedSubmodulesArray.push(constructLinkedSubmodule(linkedSubmodule, relatedSubmodule));
    }
  });

  // Append the linked submodules to the module submodules
  module.submodules = [...(module.submodules || []), ...linkedSubmodulesArray];
};

// To check if the module has Financial Projections V2
const moduleHasFinancialProjectionsV2 = (submodules: Submodule[] = []): boolean =>
  submodules.some(
    (submodule) => submodule.submoduleType === SUBMODULE_TYPE.FINANCIAL_PROJECTIONS_V2,
  );

// TO Hide Financial Projections V1 (Current Cohort) from the side nav if Financial Projections V2 (Future Cohorts) exists

const hideLegacyFinancialProjectionsV1FromSideNav = (module: Module): void => {
  if (!module.submodules?.length) return;
  // If the module does not have Financial Projections V2, return
  if (!moduleHasFinancialProjectionsV2(module.submodules)) return;

  module.submodules = module.submodules.filter(
    (submodule) => submodule.submoduleType !== SUBMODULE_TYPE.FINANCIAL_PROJECTIONS,
  );
};

const populateSubmodulesFromMetadata = (module: Module): void => {
  // If projectedSubmodules or linkedSubmodules are present in the metadata, populate the submodules from the metadata
  const { projectedSubmodules, linkedSubmodules } = getMetadata(module);
  const hasFinancialProjectionsV2 = moduleHasFinancialProjectionsV2(module.submodules);

  // When v2 exists, keep the raw v2 catalog in nav and skip v1 projected sub-views.
  if (!hasFinancialProjectionsV2) {
    processProjectedSubmodules(module, projectedSubmodules ?? []);
  }

  // Process Linked Submodules before hiding v1 so linkedSubmoduleType lookups still work.
  processLinkedSubmodules(module, linkedSubmodules ?? []);

  if (hasFinancialProjectionsV2) {
    hideLegacyFinancialProjectionsV1FromSideNav(module);
  }
};

const constructModuleObject = (module: ApiModule): Module => {
  return {
    id: module.id,
    name: module.name,
    requestStatus: 'APPROVED',
    submodules:
      module.submoduleCatalogs?.map((submodule) => ({
        id: submodule.id,
        name: submodule.name,
        url: submodule.url,
        embedId: submodule.embedId || '',
        submoduleType: submodule.submoduleType,
        defaultFilter: submodule.defaultFilter || '',
        nativeFilterId: submodule.nativeFilterId || '',
        submoduleCategory: submodule.submoduleCategory,
        hasFullAccess: submodule.hasFullAccess,
      })) || [],
    metadata: module.metadata,
  };
};

const preprocessSideNavModule = (module: Module, role: string): Module => {
  // interceptors - modify the module object before returning it (in-place functions)
  populateSubmodulesFromMetadata(module);

  // Map display name for submodules
  overrideSubmoduleDisplayName(module);

  // Hide PERMISSION-category grant markers from the side nav (all roles)
  module.submodules = module.submodules?.filter(
    (submodule) => submodule.submoduleCategory !== SubmoduleCategory.PERMISSION,
  );

  /* Role Specific Filtering and Modifications */
  if (role === 'VIEWER') {
    module.submodules = module.submodules?.filter(
      (submodule) =>
        submodule.hasFullAccess ||
        !DISALLOWED_SUBMODULE_TYPES_FOR_CLIENT.includes(submodule.submoduleType || ''),
    );
  }

  return module;
};

const preprocessModule = (module: ApiModule): Module => {
  const moduleObject: Module = constructModuleObject(module);

  return moduleObject;
};

const useUserModules = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state?.UserData);
  const [getModules, { isLoading, isError }] = useLazyGetModulesQuery();
  // const [getRequestedSubModules, { isLoading: isRequestedSubModulesLoading }] =
  //   useLazyGetRequestedSubModulesQuery();

  const getUserModules = async (isAdmin?: boolean): Promise<void> => {
    try {
      const response = await getModules().unwrap();
      // const requestedSubModules = await getRequestedSubModules().unwrap();

      let authorizedModules: Module[] = response.data.modules.map(preprocessModule);

      updateSubmoduleOrdering(authorizedModules);
      // authorizedModules = [
      //   ...authorizedModules,
      //   ...convertRequestedSubModulesToModules(requestedSubModules.data, authorizedModules),
      // ];

      let updatedAuthorizedModules =
        userDetails?.user?.role === 'ADMIN'
          ? authorizedModules.filter(
              (option) =>
                !EXCLUDED_SUBMODULE_TYPES.includes(option?.submodules?.[0]?.submoduleType || ''),
            )
          : authorizedModules;
      let updatedAuthorizedUserManagementModules = authorizedModules;

      updatedAuthorizedModules = JSON.parse(JSON.stringify(updatedAuthorizedModules)).map(
        (module: Module) => preprocessSideNavModule(module, userDetails?.user?.role),
      );

      const isDemoUser =
        userDetails.user.email &&
        DEMO_USER_EMAILS.includes(userDetails.user.email) &&
        !UserAuth.demoToken;
      const isPendingAccessUser =
        response.data.modules.some((module) =>
          module.submoduleCatalogs?.some(
            (submodule) => submodule.submoduleType === 'GETTING_STARTED',
          ),
        ) &&
        response.data.modules.every((module) =>
          module.submoduleCatalogs?.every((submodule) =>
            PENDING_ACCESS_SUBMODULE_TYPES.includes(submodule.submoduleType || ''),
          ),
        );
      if (isDemoUser) {
        updatedAuthorizedModules = [...GETTING_STARTED_DEMO_MODULES];
      } else if (isPendingAccessUser) {
        updatedAuthorizedModules = [...updatedAuthorizedModules, ...GETTING_STARTED_MODULES];
      }

      // Filter out external submodules and remove modules with no submodules
      updatedAuthorizedModules = filterExternalSubmodules(updatedAuthorizedModules);
      updatedAuthorizedModules = filterForOnePvXReportModule(updatedAuthorizedModules);

      dispatch(
        setAuthorisedModule({
          modules: updatedAuthorizedModules,
          userManagementModules: updatedAuthorizedUserManagementModules,
        }),
      );
    } catch (error) {
      console.error('Error fetching user modules:', error);
    } finally {
      dispatch(setLoadCount());
    }
  };

  return {
    getUserModules,
    isLoading: isLoading,
    isError,
  };
};

export default useUserModules;
